import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const AUTH_SESSION_KEY = "curriculum_planner_auth_session";
const AUTH_SESSION_CHANGED_EVENT = "curriculum_planner_auth_session_changed";
const DEV_USER_EMAIL_KEY = "curriculum_planner_user_email";
const DEV_USER_NAME_KEY = "curriculum_planner_user_name";

const toBool = (value, fallback = false) => {
  if (typeof value === "undefined") return fallback;
  return ["1", "true", "yes", "on"].includes(String(value).toLowerCase());
};

const shouldSendDevHeaders = () => {
  if (typeof window === "undefined") return false;
  if (typeof process.env.REACT_APP_ENABLE_DEV_HEADER_AUTH !== "undefined") {
    return toBool(process.env.REACT_APP_ENABLE_DEV_HEADER_AUTH, false);
  }

  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1";
};

const getStoredSession = () => {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(AUTH_SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      accessToken: String(parsed.accessToken || ""),
      refreshToken: String(parsed.refreshToken || ""),
      expiresIn: Number(parsed.expiresIn || 0),
      tokenType: String(parsed.tokenType || "bearer"),
      user: parsed.user || null,
      savedAt: Number(parsed.savedAt || 0),
    };
  } catch (_error) {
    return null;
  }
};

const emitAuthSessionChanged = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AUTH_SESSION_CHANGED_EVENT));
};

export const getAuthSession = () => getStoredSession();

export const setAuthSession = (session) => {
  if (typeof window === "undefined") return;

  const normalized = {
    accessToken: String(session?.accessToken || ""),
    refreshToken: String(session?.refreshToken || ""),
    expiresIn: Number(session?.expiresIn || 0),
    tokenType: String(session?.tokenType || "bearer"),
    user: session?.user || null,
    savedAt: Date.now(),
  };

  if (!normalized.accessToken) {
    window.localStorage.removeItem(AUTH_SESSION_KEY);
    emitAuthSessionChanged();
    return;
  }

  window.localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(normalized));
  emitAuthSessionChanged();
};

export const clearAuthSession = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(AUTH_SESSION_KEY);
  emitAuthSessionChanged();
};

const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  if (typeof window === "undefined") return config;

  const session = getStoredSession();
  if (session?.accessToken) {
    config.headers.Authorization = `Bearer ${session.accessToken}`;
  }

  if (!session?.accessToken && shouldSendDevHeaders()) {
    const email =
      window.localStorage.getItem(DEV_USER_EMAIL_KEY)
      || process.env.REACT_APP_DEV_USER_EMAIL
      || "";
    const name = window.localStorage.getItem(DEV_USER_NAME_KEY) || "";

    if (email) {
      config.headers["x-dev-user-email"] = email;
    }
    if (name) {
      config.headers["x-dev-user-name"] = name;
    }
  }

  return config;
});

const isAuthEndpointRequest = (url) => {
  const normalized = String(url || "").toLowerCase();
  return (
    normalized.includes("/auth/login")
    || normalized.includes("/auth/signup")
    || normalized.includes("/auth/refresh")
    || normalized.includes("/auth/forgot-password")
    || normalized.includes("/auth/reset-password")
  );
};

let refreshInFlightPromise = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (typeof window === "undefined") return Promise.reject(error);

    const originalRequest = error?.config || {};
    const status = Number(error?.response?.status || 0);
    if (status !== 401) return Promise.reject(error);

    if (isAuthEndpointRequest(originalRequest.url) || originalRequest._retry) {
      clearAuthSession();
      return Promise.reject(error);
    }

    const session = getStoredSession();
    const refreshToken = String(session?.refreshToken || "").trim();
    if (!refreshToken) {
      clearAuthSession();
      return Promise.reject(error);
    }

    try {
      if (!refreshInFlightPromise) {
        refreshInFlightPromise = axios.post(`${API_BASE}/auth/refresh`, { refreshToken });
      }
      const refreshResponse = await refreshInFlightPromise;
      const refreshedSession = refreshResponse?.data || null;
      setAuthSession(refreshedSession);
      const latest = getStoredSession();
      if (!latest?.accessToken) {
        clearAuthSession();
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${latest.accessToken}`;
      return api.request(originalRequest);
    } catch (refreshError) {
      clearAuthSession();
      return Promise.reject(refreshError);
    } finally {
      refreshInFlightPromise = null;
    }
  },
);

export const courseAPI = {
  getAll: () => api.get("/courses"),
  getById: (id) => api.get(`/courses/${id}`),
  create: (data) => api.post("/courses", data),
  update: (id, data) => api.put(`/courses/${id}`, data),
};

export const unitAPI = {
  getAll: (courseId) => api.get("/units", { params: { courseId } }),
  getById: (id) => api.get(`/units/${id}`),
  create: (data) => api.post("/units", data),
  update: (id, data) => api.put(`/units/${id}`, data),
  delete: (id) => api.delete(`/units/${id}`),
};

export const teksAPI = {
  getAll: (courseId) => api.get("/teks", { params: { courseId } }),
  getById: (id) => api.get(`/teks/${id}`),
  getAlignment: (teksCode, courseId) =>
    api.get(`/teks/alignment/${teksCode}`, { params: { courseId } }),
  create: (data) => api.post("/teks", data),
  update: (id, data) => api.put(`/teks/${id}`, data),
  delete: (id) => api.delete(`/teks/${id}`),
  bulkCreate: (data) => api.post("/teks/bulk", data),
};

export const lessonAPI = {
  getAll: (params) => api.get("/lessons", { params }),
  getById: (id) => api.get(`/lessons/${id}`),
  create: (data) => api.post("/lessons", data),
  update: (id, data) => api.put(`/lessons/${id}`, data),
  delete: (id) => api.delete(`/lessons/${id}`),
};

export const calendarAPI = {
  getRange: (courseId, startDate, endDate, extraParams = {}) =>
    api.get("/calendar", {
      params: { courseId, startDate, endDate, ...extraParams },
    }),
  getDay: (date, courseId) =>
    api.get(`/calendar/${date}`, { params: { courseId } }),
  shift: (courseId, afterDate, days) =>
    api.post("/calendar/shift", { courseId, afterDate, days }),
};

export const assetAPI = {
  getAll: (params) => api.get("/assets", { params }),
  getById: (id) => api.get(`/assets/${id}`),
  create: (data) => api.post("/assets", data),
  update: (id, data) => api.put(`/assets/${id}`, data),
  delete: (id) => api.delete(`/assets/${id}`),
};

export const pedagogyAPI = {
  getByTeks: (teksId) => api.get("/pedagogy", { params: { teksId } }),
  create: (data) => api.post("/pedagogy", data),
  update: (id, data) => api.put(`/pedagogy/${id}`, data),
  getUnitOverrides: (params) => api.get("/pedagogy/unit-overrides", { params }),
  createUnitOverride: (data) => api.post("/pedagogy/unit-overrides", data),
  updateUnitOverride: (id, data) => api.put(`/pedagogy/unit-overrides/${id}`, data),
};

export const overlayAPI = {
  getAll: (params) => api.get("/overlays", { params }),
  create: (data) => api.post("/overlays", data),
  update: (id, data) => api.put(`/overlays/${id}`, data),
  delete: (id) => api.delete(`/overlays/${id}`),
};

export const adminAPI = {
  getOverview: () => api.get("/admin/overview"),
  syncCalendarWindows: (data) => api.post("/admin/calendar-window-sync", data),
  listUsers: ({ q = "", role = "", active } = {}) =>
    api.get("/admin/users", { params: { q, role, active } }),
  updateUser: (id, data) => api.put(`/admin/users/${encodeURIComponent(id)}`, data),
  promoteToAdmin: ({ profileId, email }) =>
    api.post("/admin/promote-admin", { profileId, email }),
};

export const kioskAPI = {
  getObjectivesRange: (courseId, startDate, endDate) =>
    api.get("/kiosk/objectives", { params: { courseId, startDate, endDate } }),
  getObjectivesByDate: (courseId, date) =>
    api.get("/kiosk/objectives", { params: { courseId, date } }),
  getMyObjectivesRange: (courseId, startDate, endDate) =>
    api.get("/kiosk/my/objectives", { params: { courseId, startDate, endDate } }),
  getMyObjectivesByDate: (courseId, date) =>
    api.get("/kiosk/my/objectives", { params: { courseId, date } }),
  saveObjectivesByDate: (payload) => api.put("/kiosk/objectives", payload),
  saveMyObjectivesByDate: (payload) => api.put("/kiosk/my/objectives", payload),
  getMyChannel: (courseId) => api.get("/kiosk/my/channel", { params: { courseId } }),
  getPublished: () => api.get("/kiosk/published"),
  getPublishedPublic: (channelKey) =>
    api.get(`/kiosk/public/${encodeURIComponent(channelKey)}`, {
      params: { _ts: Date.now() },
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    }),
  getRokuState: (channelKey) =>
    api.get("/kiosk/state", {
      params: { channelKey, _ts: Date.now() },
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    }),
  publish: (payload) => api.put("/kiosk/published", payload),
  publishMy: (payload) => api.put("/kiosk/my/published", payload),
};

export const scopeSequenceAPI = {
  getAll: (courseId, schoolYear) =>
    api.get("/scope-sequence", { params: { courseId, schoolYear } }),
  update: (id, data) => api.put(`/scope-sequence/${id}`, data),
};

export const unitGuideAPI = {
  getAll: (courseId) => api.get("/unit-guides", { params: { courseId } }),
  getByUnit: (courseId, unitNumber) =>
    api.get("/unit-guides", { params: { courseId, unitNumber } }),
  create: (data) => api.post("/unit-guides", data),
  update: (id, data) => api.put(`/unit-guides/${id}`, data),
};

export const teacherAPI = {
  getMe: () => api.get("/teacher/me"),
  getProfileSettings: (schoolYear) =>
    api.get("/teacher/me/profile-settings", {
      params: schoolYear ? { schoolYear } : {},
    }),
  updateProfileSettings: (payload) => api.put("/teacher/me/profile-settings", payload),
  getPlannerEntries: (courseId, startDate, endDate) =>
    api.get("/teacher/planner", { params: { courseId, startDate, endDate } }),
  upsertPlannerEntry: (payload) => api.put("/teacher/planner", payload),
  publishPlannerEntry: (payload) => api.put("/teacher/planner/publish", payload),
  publishPlannerWeek: (payload) => api.put("/teacher/planner/publish-week", payload),
  getUnitObjectiveOverride: (courseId, unitNumber) =>
    api.get("/teacher/unit-objectives", { params: { courseId, unitNumber } }),
  upsertUnitObjectiveOverride: (payload) =>
    api.put("/teacher/unit-objectives", payload),
  listObjectiveCourses: (includeArchived = false) =>
    api.get("/teacher/objective-courses", { params: { includeArchived } }),
  createObjectiveCourse: (payload) => api.post("/teacher/objective-courses", payload),
  updateObjectiveCourse: (id, payload) =>
    api.put(`/teacher/objective-courses/${id}`, payload),
  deleteObjectiveCourse: (id) => api.delete(`/teacher/objective-courses/${id}`),
  listObjectiveEntries: (params) => api.get("/teacher/objective-entries", { params }),
  createObjectiveEntry: (payload) => api.post("/teacher/objective-entries", payload),
  updateObjectiveEntry: (id, payload) =>
    api.put(`/teacher/objective-entries/${id}`, payload),
  deleteObjectiveEntry: (id) => api.delete(`/teacher/objective-entries/${id}`),
  downloadObjectiveImportTemplate: () =>
    api.get("/teacher/objective-entries/import/template", { responseType: "text" }),
  validateObjectiveImportCsv: (payload) =>
    api.post("/teacher/objective-entries/import/validate", payload),
  importObjectiveEntriesCsv: (payload) =>
    api.post("/teacher/objective-entries/import", payload),
  listStrategies: (params) => api.get("/teacher/strategies", { params }),
  getStrategyLearnMore: (strategyId) =>
    api.get(`/teacher/strategies/${encodeURIComponent(strategyId)}/learn-more`),
  updateStrategyLearnMore: (strategyId, payload) =>
    api.put(`/teacher/strategies/${encodeURIComponent(strategyId)}/learn-more`, payload),
  createStrategy: (payload) => api.post("/teacher/strategies", payload),
  updateStrategy: (id, payload) => api.put(`/teacher/strategies/${id}`, payload),
  deleteStrategy: (id) => api.delete(`/teacher/strategies/${id}`),
};

export const formulaAPI = {
  getAll: (courseId, teksId) => api.get("/formulas", { params: { courseId, teksId } }),
};

export const performanceTaskAPI = {
  list: (courseId) =>
    api.get("/performance-tasks", {
      params: courseId ? { courseId } : {},
    }),
  getByKey: (taskKeyOrId, courseId) =>
    api.get(`/performance-tasks/${encodeURIComponent(taskKeyOrId)}`, {
      params: courseId ? { courseId } : {},
    }),
};

export const curriculumAuditAPI = {
  getOverview: () => api.get("/curriculum-audit/overview"),
  getCourse: (courseId) => api.get(`/curriculum-audit/course/${encodeURIComponent(courseId)}`),
  getResearchPacket: (courseId) =>
    api.get(`/curriculum-audit/course/${encodeURIComponent(courseId)}/research-packet`),
};

export const schoolAPI = {
  getAll: () => api.get("/walkthrough/schools"),
  create: (data) => api.post("/walkthrough/schools", data),
};

export const walkthroughAPI = {
  getDaily: ({ schoolId, date, includeDraft, publishedOnly } = {}) =>
    api.get("/walkthrough/daily", { params: { schoolId, date, includeDraft, publishedOnly } }),
  getTeacher: (teacherId, { date, schoolId, includeDraft, publishedOnly } = {}) =>
    api.get(`/walkthrough/teacher/${encodeURIComponent(teacherId)}`, {
      params: { date, schoolId, includeDraft, publishedOnly },
    }),
  getTeacherPublishedPlans: (teacherId, { schoolId, limit, offset } = {}) =>
    api.get(`/walkthrough/teacher/${encodeURIComponent(teacherId)}/published-plans`, {
      params: { schoolId, limit, offset },
    }),
  getObservations: ({ teacherId, date } = {}) =>
    api.get("/walkthrough/observations", { params: { teacherId, date } }),
  getMyReviewed: ({ dateFrom, dateTo } = {}) =>
    api.get("/walkthrough/observations", { params: { dateFrom, dateTo } }),
  getMySharedObservations: ({ dateFrom, dateTo } = {}) =>
    api.get("/walkthrough/observations", { params: { dateFrom, dateTo } }),
  saveObservation: (data) => api.post("/walkthrough/observations", data),
  updateObservation: (id, data) => api.put(`/walkthrough/observations/${encodeURIComponent(id)}`, data),
  promotePrincipal: (data) => api.post("/walkthrough/promote-principal", data),
  getPrincipalSchools: (principalId) =>
    api.get("/walkthrough/principal-schools", { params: { principalId } }),
  addPrincipalSchool: (data) => api.post("/walkthrough/principal-schools", data),
  removePrincipalSchool: (schoolId, principalId) =>
    api.delete(`/walkthrough/principal-schools/${encodeURIComponent(schoolId)}`, {
      params: { principalId },
    }),
};

export const teacherAssignmentAPI = {
  getAll: ({ schoolId } = {}) =>
    api.get("/walkthrough/teacher-assignments", { params: { schoolId } }),
  create: (data) => api.post("/walkthrough/teacher-assignments", data),
  remove: (id) => api.delete(`/walkthrough/teacher-assignments/${encodeURIComponent(id)}`),
};

export const authAPI = {
  login: ({ email, password }) => api.post("/auth/login", { email, password }),
  signup: ({ email, password }) => api.post("/auth/signup", { email, password }),
  refresh: (refreshToken) => api.post("/auth/refresh", { refreshToken }),
  forgotPassword: ({ email, redirectTo }) =>
    api.post("/auth/forgot-password", { email, redirectTo }),
  resetPassword: ({ accessToken, password }) =>
    api.post("/auth/reset-password", { accessToken, password }),
};

export const sessionStorageKeys = {
  AUTH_SESSION_KEY,
  AUTH_SESSION_CHANGED_EVENT,
  DEV_USER_EMAIL_KEY,
  DEV_USER_NAME_KEY,
};

export default api;
